import { Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { useSpeakerModerators } from '@sunrise-ui/api-client';
import {
  Colors,
  menuContentStyle,
  TextInput,
  TextReg,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { TextInput as PaperTextInput, Portal } from 'react-native-paper';

type Props = {
  eventId: string;
  selectedSpeakerId?: string;
  onSpeakerSelect: (speakerId: string | undefined) => void;
};

export function SpeakerFilter({
  eventId,
  selectedSpeakerId,
  onSpeakerSelect,
}: Props) {
  const styles = useDynamicStyles(createStyles);

  // Use the new hook to get all speakers/moderators for the event
  const { speakerModeratorOptions, isLoading: isLoadingProfiles } =
    useSpeakerModerators(eventId);

  // Internal state for search functionality
  const [speakerSearchText, setSpeakerSearchText] = useState('');
  const [isSpeakerMenuOpen, setIsSpeakerMenuOpen] = useState(false);
  const dropdownRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Open menu with proper positioning
  const openMenu = () => {
    if (dropdownRef.current) {
      dropdownRef.current.measureInWindow((x, y, width, height) => {
        setMenuPosition({
          top: y + height,
          left: x,
          width: width,
        });
        setIsSpeakerMenuOpen(true);
      });
    }
  };

  // Close menu
  const closeMenu = () => {
    setIsSpeakerMenuOpen(false);
  };

  // Handle selecting a speaker from search results
  const handleSelectSpeaker = useCallback(
    (speakerId: string) => {
      onSpeakerSelect(speakerId);
      setSpeakerSearchText(''); // Clear search text when a speaker is selected
      closeMenu();
    },
    [onSpeakerSelect],
  );

  // Filter speaker options based on search text
  const filteredSpeakers = useMemo(() => {
    if (!speakerSearchText.trim()) {
      return speakerModeratorOptions;
    }
    const searchLower = speakerSearchText.toLowerCase();
    return speakerModeratorOptions.filter((option) =>
      option.label.toLowerCase().includes(searchLower),
    );
  }, [speakerModeratorOptions, speakerSearchText]);

  // Get currently selected speaker name
  const selectedSpeakerName = useMemo(() => {
    if (!selectedSpeakerId) return '';
    const speaker = speakerModeratorOptions.find(
      (option) => option.value === selectedSpeakerId,
    );
    return speaker ? speaker.label : '';
  }, [selectedSpeakerId, speakerModeratorOptions]);

  return (
    <View ref={dropdownRef} style={styles.searchInputContainer}>
      {/* Show selected speaker name or search field */}
      {selectedSpeakerId ? (
        <View style={styles.selectedSpeakerContainer}>
          {/* Keep magnifier icon visible when speaker is selected */}
          <View style={styles.selectedIconContainer}>
            <HugeiconsIcon icon={Search01Icon} color={Colors.text} size={20} />
          </View>
          <TextReg numberOfLines={1} style={styles.selectedSpeakerText}>
            {selectedSpeakerName}
          </TextReg>
          <TouchableOpacity
            onPress={() => onSpeakerSelect(undefined)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={styles.clearButtonContainer}
          >
            <TextReg style={styles.clearText}>✕</TextReg>
          </TouchableOpacity>
        </View>
      ) : (
        <TextInput
          label="Search speakers..."
          showLabel={false}
          placeholder="Search speakers..."
          value={speakerSearchText}
          onChange={(text) => {
            setSpeakerSearchText(text);
            if (text.trim()) {
              openMenu();
            } else {
              closeMenu();
            }
          }}
          style={styles.searchInput}
          autoCorrect={false}
          onFocus={() => {
            if (speakerSearchText.trim()) {
              openMenu();
            }
          }}
          left={
            <PaperTextInput.Icon
              icon={() => (
                <HugeiconsIcon
                  icon={Search01Icon}
                  color={Colors.text}
                  size={20}
                />
              )}
              loading={isLoadingProfiles}
            />
          }
        />
      )}

      {/* Portal-based dropdown to prevent clipping */}
      <Portal>
        {isSpeakerMenuOpen && speakerSearchText && (
          <>
            {/* Backdrop to detect outside clicks */}
            <TouchableOpacity
              style={styles.backdrop}
              activeOpacity={1}
              onPress={closeMenu}
            />
            <View
              style={[
                styles.portalDropdownMenu,
                {
                  top: menuPosition.top,
                  left: menuPosition.left,
                  width: menuPosition.width,
                },
              ]}
            >
              {filteredSpeakers.length > 0 ? (
                filteredSpeakers.map((speaker, index) => (
                  <TouchableOpacity
                    key={speaker.value}
                    style={[
                      styles.dropdownItem,
                      index === filteredSpeakers.length - 1 &&
                        styles.lastDropdownItem,
                    ]}
                    onPress={() => {
                      handleSelectSpeaker(speaker.value);
                    }}
                  >
                    <TextReg numberOfLines={1}>{speaker.label}</TextReg>
                  </TouchableOpacity>
                ))
              ) : (
                <View style={[styles.dropdownItem, styles.lastDropdownItem]}>
                  <TextReg style={styles.noResultsText}>
                    No speakers found
                  </TextReg>
                </View>
              )}
            </View>
          </>
        )}
      </Portal>
    </View>
  );
}

function createStyles() {
  return StyleSheet.create({
    searchInputContainer: {
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: 180,
      maxWidth: 240,
    },
    selectedSpeakerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: Colors.opacity05,
      borderRadius: 24,
      height: 40,
      minHeight: 40,
      maxHeight: 40,
      paddingHorizontal: 17,
      borderWidth: 1,
      borderColor: Colors.opacity20,
      flex: 1,
      minWidth: 0,
    },
    selectedIconContainer: {
      marginRight: 16,
      flexShrink: 0,
    },
    selectedSpeakerText: {
      fontSize: 14,
      fontWeight: '500',
      color: Colors.text,
      flex: 1,
      minWidth: 0,
    },
    clearButtonContainer: {
      marginLeft: 8,
      flexShrink: 0,
    },
    clearText: {
      fontSize: 16,
      color: Colors.text,
      fontWeight: 'bold',
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      backgroundColor: Colors.opacity05,
      height: 40,
      minHeight: 40,
      maxHeight: 40,
    },
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      ...menuContentStyle,
    },
    portalDropdownMenu: {
      position: 'absolute',
      zIndex: 1000,
      borderWidth: 1,
      borderColor: Colors.opacity10,
      ...menuContentStyle,
    },
    dropdownItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: Colors.opacity10,
    },
    lastDropdownItem: {
      borderBottomWidth: 0,
    },
    noResultsText: {
      fontStyle: 'italic',
      color: Colors.placeholderText,
      textAlign: 'center',
    },
    backdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 999,
    },
  });
}
